import {
  Bar,
  BarChart,
  CartesianGrid,
  Customized,
  createHistogramBuckets,
  HistogramBucket,
  Label,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'chart';
import { useTranslation } from 'i18n';
import { useMemo } from 'react';
import { Box, formatNumber, Stack, Typography, useBreakpoint, useTheme } from 'ui';

const formatPercentValue = (value: number) => `${formatNumber(value, { maximumFractionDigits: 1 })}%`;

interface AxisMap {
  [key: string]: {
    scale: {
      (value: string): number;
      bandwidth?: () => number;
      range: () => [number, number];
      domain: () => string[];
    };
  };
}

interface AverageLineProps {
  xAxisMap?: AxisMap;
  yAxisMap?: AxisMap;
  offset?: { top: number; bottom: number; left: number; right: number };
  targetGrade: number;
  stroke: string;
  fontSize: number;
  histogramData: HistogramBucket[];
}

const AverageLine = ({
  xAxisMap,
  yAxisMap,
  offset,
  targetGrade,
  stroke,
  fontSize,
  histogramData,
}: AverageLineProps) => {
  if (!xAxisMap || !yAxisMap || !offset || !histogramData.length) return null;

  const xAxisEntry = Object.values(xAxisMap)[0];
  const yAxisEntry = Object.values(yAxisMap)[0];

  if (!xAxisEntry?.scale || !yAxisEntry?.scale) return null;

  const xScale = xAxisEntry.scale;
  const yScale = yAxisEntry.scale;

  const domain = xScale.domain();

  // Find which bucket the average falls into
  let bucketIndex = -1;
  let bucketStartPercent = 0;
  let bucketEndPercent = 100;

  for (let i = 0; i < histogramData.length; i++) {
    const bucket = histogramData[i];
    const nextBucket = histogramData[i + 1];

    const bucketStart = bucket.percentage;
    const bucketEnd = nextBucket ? nextBucket.percentage : 100;

    // Check if targetGrade falls within this bucket's range
    if (targetGrade >= bucketStart && (targetGrade < bucketEnd || i === histogramData.length - 1)) {
      bucketIndex = i;
      bucketStartPercent = bucketStart;
      bucketEndPercent = bucketEnd;
      break;
    }
  }

  // If we couldn't find a bucket, default to the last one
  if (bucketIndex === -1) {
    bucketIndex = histogramData.length - 1;
    bucketStartPercent = histogramData[bucketIndex].percentage;
    bucketEndPercent = 100;
  }

  // Get the x position of this bucket
  const bucketLabel = domain[bucketIndex];
  const bucketCenterX = xScale(bucketLabel);

  // Calculate bandwidth (width of each bar)
  const bandwidth = xScale.bandwidth ? xScale.bandwidth() : 0;

  // Calculate position within the bucket
  const bucketRange = bucketEndPercent - bucketStartPercent;
  const positionInBucket = bucketRange > 0 ? (targetGrade - bucketStartPercent) / bucketRange : 0.5;

  // Position within the bar (centered around the bar's center)
  const x = bucketCenterX + (positionInBucket - 0.5) * bandwidth;

  const yTop = yScale.range()[0];
  const yBottom = yScale.range()[1];

  return (
    <g>
      <line x1={x} y1={yBottom} x2={x} y2={yTop} stroke={stroke} strokeWidth={2} strokeDasharray="5 5" />
      <text x={x} y={yBottom - 10} textAnchor="middle" fill={stroke} fontSize={fontSize} fontWeight="bold">
        {formatPercentValue(targetGrade)}
      </text>
    </g>
  );
};

interface TargetBaleWeightsHistogramProps {
  targetBaleWeights: number[];
  targetGrade: number;
  title?: string;
}

const CustomLegend = ({
  averageLabel,
  balesLabel,
  isMobile,
}: {
  averageLabel: string;
  balesLabel: string;
  isMobile: boolean;
}) => {
  return (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 1,
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 1,
        paddingTop: 0,
        marginBottom: isMobile ? 2 : 4,
      }}
    >
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Box
          sx={[
            {
              fontSize: 8,
              width: 10,
              height: 10,
            },
            ({ palette }) => ({
              backgroundColor: palette.primary[300],
            }),
          ]}
        />
        {balesLabel}
      </Stack>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Box
          sx={[
            {
              fontSize: 8,
              width: 20,
              height: 2,
            },
            ({ palette }) => ({
              backgroundColor: palette.chart.main,
            }),
          ]}
        />
        {averageLabel}
      </Stack>
    </Stack>
  );
};

export const TargetBaleWeightsHistogram = ({
  targetBaleWeights,
  targetGrade,
  title,
}: TargetBaleWeightsHistogramProps) => {
  const { t } = useTranslation('features');
  const { isMobile } = useBreakpoint();
  const theme = useTheme();
  const totalBuckets = isMobile ? 7 : 11;
  const histogramData = useMemo(() => {
    return createHistogramBuckets(targetBaleWeights, targetGrade, totalBuckets);
  }, [targetBaleWeights, targetGrade, totalBuckets]);

  const chartHeight = isMobile ? 250 : 300;
  const fontSize = isMobile ? 12 : 16;
  const barSize = isMobile ? 20 : 25;
  const yAxisWidth = isMobile ? 40 : 60;
  const xAxisPadding = isMobile ? { left: 15, right: 15 } : { left: 30, right: 30 };
  const balesLabel = t('charts.targetBaleWeightsHistogram.labels.bales');
  const averageLabel = t('charts.targetBaleWeightsHistogram.labels.average', {
    values: { targetGrade: targetGrade.toFixed(1) },
  });

  return (
    <Box
      data-testid="target-bale-weights-histogram"
      sx={{
        width: '100%',
      }}
    >
      {title ? (
        <Typography variant={isMobile ? 'subtitle1' : 'h6'} align="center" sx={{ mb: 2 }}>
          {title}
        </Typography>
      ) : null}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={histogramData}
          barSize={barSize}
          barCategoryGap={0}
          layout="horizontal"
          barGap={0}
          margin={{ bottom: 30, left: 10, right: 10, top: 10 }}
        >
          <Legend
            verticalAlign="top"
            content={<CustomLegend averageLabel={averageLabel} balesLabel={balesLabel} isMobile={isMobile} />}
          />
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis dataKey="label" scale="point" padding={xAxisPadding} tickSize={5} fontSize={fontSize} interval={0}>
            <Label value={t('charts.targetBaleWeightsHistogram.axes.x')} position="bottom" offset={15} />
          </XAxis>
          <YAxis allowDecimals={false} tickMargin={isMobile ? 5 : 15} width={yAxisWidth} fontSize={fontSize}>
            <Label value={t('charts.targetBaleWeightsHistogram.axes.y')} position="left" angle={-90} dy={-50} />
          </YAxis>
          <Tooltip
            formatter={(value) =>
              t('charts.targetBaleWeightsHistogram.tooltip.value', { values: { value: Number(value ?? 0) } })
            }
            labelFormatter={(label) => t('charts.targetBaleWeightsHistogram.tooltip.label', { values: { label } })}
            cursor={{ fill: theme.palette.action.hover }}
          />
          <Bar
            dataKey="count"
            name={balesLabel}
            radius={[4, 4, 0, 0]}
            barSize={barSize}
            fill={theme.palette.primary[300]}
          />
          <Customized
            component={
              <AverageLine
                targetGrade={targetGrade}
                stroke={theme.palette.chart.main}
                fontSize={fontSize}
                histogramData={histogramData}
              />
            }
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
